import { buildGrossAllocation } from "./buildGrossAllocation";

const INR = new Intl.NumberFormat("en-IN", {
  style: "currency",
  currency: "INR",
  maximumFractionDigits: 0,
});

function showAmount(usingMoney, amount) {
  if (!usingMoney) return `${Math.round(Number(amount) || 0)}%`;
  return INR.format(Math.round(Number(amount) || 0));
}

export default function StructureGrossPanel({
  components = [],
  formulas = {},
  monthlyGross = "",
  onMonthlyGrossChange,
  compact = false,
}) {
  const grossNum = Number(String(monthlyGross || "").replace(/,/g, ""));
  const model = buildGrossAllocation(components, formulas, grossNum > 0 ? grossNum : null);
  const slices = [...model.earnings, ...model.deductions, ...model.employerContributions].filter(
    (r) => r.amount > 0,
  );

  return (
    <section className={`stp-gross${compact ? " stp-gross--compact" : ""}`} aria-label="Gross salary allocation">
      <div className="stp-gross-head">
        <div>
          <h3 className="stp-section-title">Allocation</h3>
          {compact ? null : (
            <p className="stp-section-hint">
              Gross = earnings. Net = Gross − employee deductions. Employer contributions do not reduce Net.
            </p>
          )}
        </div>
        {compact ? null : (
          <label className="stp-gross-input">
            <span>Monthly gross</span>
            <input
              className="stp-input"
              inputMode="numeric"
              placeholder="Optional — e.g. 50000"
              value={monthlyGross}
              onChange={(e) => onMonthlyGrossChange?.(e.target.value)}
              aria-label="Monthly gross salary"
            />
          </label>
        )}
      </div>

      <p className="stp-gross-base">
        {model.usingMoney
          ? `Monthly gross ${INR.format(Math.round(model.base))}`
          : "Enter monthly gross to see amounts"}
      </p>

      <div className="stp-gross-bar" aria-hidden>
        {slices.map((row) => (
          <span
            key={row.id}
            className={row.bucket === "earning" ? "is-earn" : row.bucket === "employer" ? "is-er" : "is-ded"}
            style={{ width: `${Math.max(row.percent, 0)}%` }}
            title={`${row.name} ${row.percent}%`}
          />
        ))}
      </div>

      <div className="stp-gross-cols stp-gross-cols--3">
        <div>
          <h4>Earnings</h4>
          <ul>
            {model.earnings.map((row) => (
              <li key={row.id}>
                <span>{row.name}</span>
                <strong>{showAmount(model.usingMoney, row.amount)}</strong>
                <small>{row.calculation || row.note || "—"}</small>
              </li>
            ))}
            {!model.earnings.length ? <li className="stp-section-hint">No earnings selected</li> : null}
          </ul>
        </div>
        <div>
          <h4>Employee Deductions</h4>
          <ul>
            {model.deductions.map((row) => (
              <li key={row.id}>
                <span>{row.name}</span>
                <strong>{showAmount(model.usingMoney, row.amount)}</strong>
                <small>{row.calculation || row.note || "—"}</small>
              </li>
            ))}
            {!model.deductions.length ? <li className="stp-section-hint">No deductions selected</li> : null}
          </ul>
        </div>
        <div>
          <h4>Employer Contributions</h4>
          <ul>
            {model.employerContributions.map((row) => (
              <li key={row.id}>
                <span>{row.name}</span>
                <strong>{showAmount(model.usingMoney, row.amount)}</strong>
                <small>{row.calculation || row.note || "—"}</small>
              </li>
            ))}
            {!model.employerContributions.length ? (
              <li className="stp-section-hint">None in this structure</li>
            ) : null}
          </ul>
        </div>
      </div>

      <div className="stp-gross-summary" aria-label="Salary totals">
        <div className="stp-gross-summary-row">
          <span>Total Earnings / Gross</span>
          <strong>{showAmount(model.usingMoney, model.earningsTotal)}</strong>
        </div>
        <div className="stp-gross-summary-row">
          <span>Employee Deductions</span>
          <strong>{showAmount(model.usingMoney, model.deductionsTotal)}</strong>
        </div>
        <div className="stp-gross-summary-row stp-gross-summary-row--net">
          <span>Net Salary</span>
          <strong>{showAmount(model.usingMoney, model.netSalary)}</strong>
        </div>
        <div className="stp-gross-summary-row">
          <span>Employer Contributions</span>
          <strong>{showAmount(model.usingMoney, model.employerTotal)}</strong>
        </div>
        <div className="stp-gross-summary-row stp-gross-summary-row--ctc">
          <span>Gross (earnings only)</span>
          <strong>{showAmount(model.usingMoney, model.earningsTotal)}</strong>
        </div>
        {model.employerTotal > 0 ? (
          <div className="stp-gross-summary-row">
            <span>Employer items (not in Gross)</span>
            <strong>{showAmount(model.usingMoney, model.employerTotal)}</strong>
          </div>
        ) : null}
      </div>
    </section>
  );
}
